<template>
  <div v-if="showInstallButton || isInstalled" class="pwa-install-container">
    <button
      @click="handleButtonClick"
      class="pwa-install-button"
      :disabled="installing || isInstalled"
      :class="{ 'pwa-button-installed': isInstalled }"
    >
      <svg 
        v-if="isInstalled" 
        class="pwa-install-icon" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          stroke-linecap="round" 
          stroke-linejoin="round" 
          stroke-width="2" 
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <svg 
        v-else-if="!installing" 
        class="pwa-install-icon" 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path 
          stroke-linecap="round" 
          stroke-linejoin="round" 
          stroke-width="2" 
          d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
      <svg 
        v-else 
        class="pwa-install-icon animate-spin" 
        fill="none" 
        viewBox="0 0 24 24"
      >
        <circle 
          class="opacity-25" 
          cx="12" 
          cy="12" 
          r="10" 
          stroke="currentColor" 
          stroke-width="4"
        />
        <path 
          class="opacity-75" 
          fill="currentColor" 
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span class="pwa-install-text">
        {{ buttonText }}
      </span>
    </button>
    
    <!-- Dismiss button - only show if not installed and not alwaysShow -->
    <button 
      v-if="!isInstalled && !alwaysShow"
      @click="dismissInstallPrompt" 
      class="pwa-dismiss-button"
      title="Dismiss"
    >
      <svg class="pwa-dismiss-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  name: 'PWAInstallButton',
  props: {
    alwaysShow: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'default' // 'default' or 'settings'
    }
  },
  data() {
    return {
      deferredPrompt: null,
      showInstallButton: false,
      installing: false,
      isDismissed: false,
      isInstalled: false
    }
  },
  computed: {
    buttonText() {
      if (this.isInstalled) {
        return 'App Installed';
      }
      if (this.installing) {
        return 'Installing...';
      }
      return 'Install App';
    }
  },
  mounted() {
    this.initializeInstallPrompt();
    
    // Store reference to beforeinstallprompt listener for checking uninstall
    this.beforeInstallPromptFired = false;
    
    const beforeInstallHandler = () => {
      this.beforeInstallPromptFired = true;
    };
    
    window.addEventListener('beforeinstallprompt', beforeInstallHandler);
    
    // Check install status periodically (every 2 seconds when page is visible)
    this.installCheckInterval = setInterval(() => {
      const wasInstalled = this.isInstalled;
      const nowInstalled = this.checkIfAlreadyInstalled();
      
      // If beforeinstallprompt fired, it means app was uninstalled
      if (this.beforeInstallPromptFired && wasInstalled) {
        this.isInstalled = false;
        this.showInstallButton = true;
        this.beforeInstallPromptFired = false; // Reset flag
        return;
      }
      
      // If installation status changed, update UI
      if (wasInstalled !== nowInstalled) {
        this.isInstalled = nowInstalled;
        
        if (nowInstalled) {
          this.showInstallButton = this.alwaysShow; // Show in settings, hide elsewhere
          this.$emit('app-installed');
        } else {
          this.showInstallButton = true;
        }
      }
    }, 2000);
  },
  beforeUnmount() {
    // Clean up interval
    if (this.installCheckInterval) {
      clearInterval(this.installCheckInterval);
    }
  },
  methods: {
    handleButtonClick() {
      if (this.isInstalled) {
        return;
      }
      this.installPWA();
    },

    initializeInstallPrompt() {
      this.isDismissed = localStorage.getItem('pwa-install-dismissed') === 'true';
      const alreadyInstalled = this.checkIfAlreadyInstalled();
      this.isInstalled = alreadyInstalled;

      if (alreadyInstalled) {
        this.showInstallButton = this.alwaysShow;
      }

      if (window.deferredPrompt) {
        this.deferredPrompt = window.deferredPrompt;
        if (!this.isDismissed || this.alwaysShow) {
          this.showInstallButton = true;
        }
      }

      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        this.deferredPrompt = e;
        localStorage.removeItem('pwa-ever-installed');
        localStorage.removeItem('pwa-last-standalone-check');
        this.isInstalled = false;
        if (!this.isDismissed || this.alwaysShow) {
          this.showInstallButton = true;
        }
      });

      setTimeout(() => {
        if (this.alwaysShow && !this.showInstallButton) {
          this.showInstallButton = true;
        } else if (!this.deferredPrompt && !this.showInstallButton && !this.isInstalled && !this.isDismissed) {
          this.showInstallButton = true;
        }
      }, 3000);

      window.addEventListener('appinstalled', () => {
        this.isInstalled = true;
        this.showInstallButton = this.alwaysShow; // Keep visible in settings
        this.deferredPrompt = null;
        
        // Mark as installed in localStorage for cross-context detection
        localStorage.setItem('pwa-ever-installed', 'true');
        localStorage.setItem('pwa-last-standalone-check', Date.now().toString());
        
        // Show success message (optional)
        this.$emit('app-installed');
      });
    },

    checkIfAlreadyInstalled() {
      // First check if running in standalone mode (currently in installed app)
      const isStandalone = 
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
        (window.navigator.standalone === true) ||
        (window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches) ||
        (window.matchMedia && window.matchMedia('(display-mode: fullscreen)').matches);

      if (isStandalone) {
        localStorage.setItem('pwa-ever-installed', 'true');
        localStorage.setItem('pwa-last-standalone-check', Date.now().toString());
        return true;
      }

      // If in browser, check if app was ever installed on this device
      const wasEverInstalled = localStorage.getItem('pwa-ever-installed') === 'true';
      const lastCheck = localStorage.getItem('pwa-last-standalone-check');
      
      // Only trust recent standalone checks (within last 24 hours)
      // This ensures uninstalls are detected quickly
      if (wasEverInstalled && lastCheck) {
        const timeSinceLastCheck = Date.now() - parseInt(lastCheck);
        const hoursAgo = timeSinceLastCheck / (1000 * 60 * 60);
        
        // Only consider installed if used in standalone mode within last 24 hours
        if (hoursAgo < 24) {
          return true;
        } else {
          localStorage.removeItem('pwa-ever-installed');
          localStorage.removeItem('pwa-last-standalone-check');
          return false;
        }
      }

      return false;
    },

    async installPWA() {
      if (!this.deferredPrompt) {
        this.showManualInstallInstructions();
        return;
      }

      this.installing = true;

      try {
        this.deferredPrompt.prompt();
        await this.deferredPrompt.userChoice;
        this.deferredPrompt = null;
        this.showInstallButton = false;
      } catch (error) {
        console.error('PWA install failed:', error);
        // Fallback to manual instructions
        this.showManualInstallInstructions();
      } finally {
        this.installing = false;
      }
    },

    showManualInstallInstructions() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      const isEdge = /Edge|Edg/.test(navigator.userAgent);
      
      let message = '';
      
      if (isIOS) {
        message = 'To install this app on iOS:\n1. Tap the Share button\n2. Select "Add to Home Screen"';
      } else if (isAndroid) {
        message = 'To install this app:\n1. Tap the menu (⋮)\n2. Select "Add to Home Screen" or "Install App"';
      } else if (isEdge) {
        message = 'To install this app in Edge:\n1. Click the menu (⋯) → Apps → "Install this site as an app"\n2. Or look for the install icon in the address bar';
      } else {
        message = 'To install this app:\n1. Look for the install icon in your browser\'s address bar\n2. Or check your browser\'s menu for "Install" option';
      }
      
      alert(message);
      this.dismissInstallPrompt();
    },

    dismissInstallPrompt() {
      if (this.alwaysShow) {
        return;
      }
      
      this.showInstallButton = false;
      this.isDismissed = true;
      
      // Remember the dismissal for this session
      localStorage.setItem('pwa-install-dismissed', 'true');
      
      this.$emit('install-dismissed');
    }
  }
}
</script>

<style scoped>
.pwa-install-container {
  @apply relative inline-flex items-center text-white rounded-lg shadow-lg transition-all duration-200 overflow-hidden;
}

.pwa-install-container:has(.pwa-button-installed) {
  @apply bg-green-600;
}

.pwa-install-container:not(:has(.pwa-button-installed)) {
  @apply bg-blue-600 hover:bg-blue-700;
}

.pwa-button-installed {
  @apply cursor-default;
}

.pwa-install-button {
  @apply flex items-center space-x-2 px-4 py-2 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed;
}

.pwa-install-icon {
  @apply w-5 h-5;
}

.pwa-dismiss-button {
  @apply flex items-center justify-center w-8 h-8 bg-blue-700 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200;
}

.pwa-dismiss-icon {
  @apply w-4 h-4;
}

.pwa-install-text {
  @apply select-none;
}

/* Animation for spinning loader */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>